import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { formatDayMonth } from '../../../utils/dateFormat';
import { humanize } from '../../Marketing/shared';
import { Card, CardHeader } from '../../../components/ui/Card';
import { Alert } from '../../../components/ui/Alert';
import { useContentCalendar, useDraftProposal, useDismissProposal } from '../../../hooks/useGtm';
import type { ContentCalendarProposal } from '../../../api/gtm';
import { WEEKDAYS, isoLocal, mondayOf } from '../../../utils/calendarGrid';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { SegmentedControl } from '../../../components/ui/Tabs';
import { Badge } from '../../../components/ui/Badge';

const WEEKS_SHOWN = 4;
/** With this few proposals in the four weeks, a calendar grid is mostly empty cells. */
const SPARSE_MAX = 3;
const LIST_DAYS = 14;

/** `iso` is the local-day helper, shared with the organizer calendar. */
const iso = isoLocal;

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Failed to start draft.';
}

export function ContentCalendar() {
  const [weekOffset, setWeekOffset] = useState(0);
  // null = decide from the data: a two-week list when the four weeks are sparse.
  const [layout, setLayout] = useState<'grid' | 'list' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const gridStart = useMemo(() => {
    const base = mondayOf(new Date());
    base.setDate(base.getDate() + weekOffset * 7);
    return base;
  }, [weekOffset]);

  const days = useMemo(() => {
    const out: Date[] = [];
    for (let i = 0; i < WEEKS_SHOWN * 7; i++) {
      const d = new Date(gridStart);
      d.setDate(d.getDate() + i);
      out.push(d);
    }
    return out;
  }, [gridStart]);

  const from = iso(days[0]!);
  const to = iso(days[days.length - 1]!);
  const today = iso(new Date());

  const calendar = useContentCalendar(from, to);
  const { data, isLoading } = calendar;

  const proposalsByDate = useMemo(() => {
    const map = new Map<string, ContentCalendarProposal[]>();
    for (const p of data?.proposals ?? []) {
      (map.get(p.proposedDate) ?? map.set(p.proposedDate, []).get(p.proposedDate)!).push(p);
    }
    return map;
  }, [data]);

  // Key for the two-letter tags actually on screen.
  const typesShown = useMemo(() => {
    const types = new Map<string, string>();
    for (const p of data?.proposals ?? []) types.set(typeTag(p.suggestedContentType), humanize(p.suggestedContentType));
    return [...types.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);
  const anyScore = (data?.proposals ?? []).some((p) => p.opportunityScore != null);

  const proposals = data?.proposals ?? [];
  const view = layout ?? (proposals.length <= SPARSE_MAX ? 'list' : 'grid');
  const listEnd = iso(days[LIST_DAYS - 1]!);
  const listed = [...proposals]
    .filter((p) => p.proposedDate <= listEnd)
    .sort((a, b) => a.proposedDate.localeCompare(b.proposedDate));
  const later = proposals.length - listed.length;
  // Weeks on screen with nothing slotted, so the gap against "a few a week" is explained, not just empty.
  const shownWeeks = view === 'list' ? LIST_DAYS / 7 : WEEKS_SHOWN;
  const emptyWeeks = Array.from({ length: shownWeeks }, (_, w) => {
    const start = iso(days[w * 7]!);
    const end = iso(days[w * 7 + 6]!);
    return proposals.some((p) => p.proposedDate >= start && p.proposedDate <= end) ? null : start;
  }).filter((d): d is string => d !== null);
  const overdue = proposals.filter((p) => p.proposedDate < today).length;

  const monthLabel =
    view === 'list'
      ? `${formatDayMonth(days[0]!)} – ${formatDayMonth(days[LIST_DAYS - 1]!)}`
      : `${formatDayMonth(days[0]!)} – ${formatDayMonth(days[days.length - 1]!)}`;

  const onError = (msg: string) => { setNotice(null); setError(msg); };
  const onDrafted = () => { setError(null); setNotice('Draft started: find it in the content engine.'); };

  // Nothing slotted in the coming weeks: one line instead of a card of empty weeks and notes.
  if (!calendar.error && !isLoading && proposals.length === 0 && weekOffset === 0) {
    return (
      <Card padding="sm">
        <p className="text-body-sm text-text-secondary">
          <span className="font-medium text-text-primary">Content calendar:</span> nothing is recommended for {monthLabel}.{' '}
          <span className="text-text-tertiary">
            Sales and CS calls are turned into recommendations each Monday.
          </span>{' '}
          <button type="button" onClick={() => setWeekOffset(WEEKS_SHOWN)} className="text-body-xs text-accent hover:underline">
            Look further ahead
          </button>
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        title="Content calendar"
        description="Recommendations from sales and CS calls (up to 3 a week), made each Monday for the days ahead. Peec gaps now go to the refresh list above. Draft or dismiss each one."
        action={
        <div className="flex items-center gap-2">
          <button type="button" aria-label="Previous week" onClick={() => setWeekOffset((w) => w - 1)} className="flex h-7 w-7 items-center justify-center rounded border border-border-light text-text-secondary hover:bg-surface-secondary"><ChevronLeft className="h-4 w-4" aria-hidden /></button>
          <span className="text-body-sm text-text-secondary tabular-nums">{monthLabel}</span>
          <button type="button" aria-label="Next week" onClick={() => setWeekOffset((w) => w + 1)} className="flex h-7 w-7 items-center justify-center rounded border border-border-light text-text-secondary hover:bg-surface-secondary"><ChevronRight className="h-4 w-4" aria-hidden /></button>
          {weekOffset !== 0 && (
            <button type="button" onClick={() => setWeekOffset(0)} className="rounded border border-border-light px-3 py-1 text-body-xs text-text-secondary hover:bg-surface-secondary">Today</button>
          )}
          <SegmentedControl
            ariaLabel="Calendar layout"
            options={[
              { id: 'list', label: '2-week list' },
              { id: 'grid', label: '4-week grid' },
            ]}
            value={view}
            onChange={setLayout}
          />
        </div>
        }
      />

      {error && (
        <Alert variant="error" dismissible onClose={() => setError(null)} className="mt-3">{error}</Alert>
      )}
      {notice && (
        <Alert variant="success" dismissible onClose={() => setNotice(null)} className="mt-3">{notice}</Alert>
      )}

      {calendar.error ? (
        <div className="mt-4">
          <ErrorState title="Couldn’t load the content calendar" error={calendar.error} onRetry={() => calendar.refetch()} />
        </div>
      ) : isLoading ? (
        <LoadingState />
      ) : view === 'list' ? (
        <div className="mt-4">
          {listed.length === 0 ? (
            <p className="text-body-sm text-text-tertiary">No recommendations in these two weeks.</p>
          ) : (
            <ul className="divide-y divide-border-light">
              {listed.map((p) => (
                <li key={p.id} className="flex items-start gap-3 py-2.5">
                  <span className="w-20 flex-none text-body-xs tabular-nums text-text-tertiary">
                    {formatDayMonth(new Date(`${p.proposedDate}T00:00:00`))}
                  </span>
                  <div className="min-w-0 flex-1">
                    <ProposalChip proposal={p} onError={onError} onDrafted={onDrafted} flat overdue={p.proposedDate < today} />
                  </div>
                </li>
              ))}
            </ul>
          )}
          {later > 0 && (
            <p className="mt-2 text-body-xs text-text-tertiary">
              {later} more in the two weeks after.{' '}
              <button type="button" onClick={() => setLayout('grid')} className="text-accent hover:underline">
                Show the 4-week grid
              </button>
            </p>
          )}
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-7 gap-px overflow-hidden rounded border border-border-light bg-border-light">
          {WEEKDAYS.map((w) => (
            <div key={w} className="eyebrow bg-surface-primary px-2 py-1 text-center">{w}</div>
          ))}
          {days.map((d) => {
            const key = iso(d);
            const isToday = key === today;
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            return (
              <div
                key={key}
                className={`min-h-[96px] p-1.5 ${isWeekend ? 'bg-surface-secondary/60' : 'bg-surface-primary'} ${isToday ? 'ring-2 ring-inset ring-accent' : ''}`}
              >
                <div className={`text-body-xs ${isToday ? 'font-semibold text-accent' : 'text-text-tertiary'}`}>
                  {d.getDate()}
                </div>
                <div className="mt-1 space-y-1">
                  {(proposalsByDate.get(key) ?? []).map((p) => (
                    <ProposalChip key={p.id} proposal={p} onError={onError} onDrafted={onDrafted} overdue={key < today} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!calendar.error && !isLoading && (emptyWeeks.length > 0 || overdue > 0) && (
        <p className="mt-3 text-body-xs text-text-tertiary">
          {[
            overdue > 0 &&
              `${overdue} ${overdue === 1 ? 'recommendation is' : 'recommendations are'} past ${overdue === 1 ? 'its' : 'their'} date and still open: draft or dismiss ${overdue === 1 ? 'it' : 'them'}.`,
            emptyWeeks.length > 0 &&
              `${emptyWeeks.length === shownWeeks ? `None of these ${shownWeeks} weeks has a recommendation` : `${emptyWeeks.length} of ${shownWeeks} weeks have no recommendations`} (${emptyWeeks.length === 1 ? 'week' : 'weeks'} of ${emptyWeeks.map((d) => formatDayMonth(new Date(`${d}T00:00:00`))).join(', ')}). Recommendations are made on Mondays for the days just ahead, so later weeks fill in on their own Monday. A week also stays empty when earlier recommendations are still open (they count against the weekly cap) or the calls had nothing new.`,
          ]
            .filter(Boolean)
            .join(' ')}
        </p>
      )}
      {!calendar.error && !isLoading && (typesShown.length > 0 || anyScore) && (
        <p className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-body-xs text-text-tertiary">
          {typesShown.map(([tag, label]) => (
            <span key={tag} className="flex items-center gap-1">
              <span className="rounded-sm bg-surface-tertiary px-1 font-label text-[10px] leading-4 text-text-secondary">{tag}</span>
              {label}
            </span>
          ))}
          {anyScore && <span>Number: Peec opportunity score, higher means a bigger gap to close</span>}
        </p>
      )}
    </Card>
  );
}

/** "blog_post" → "BP", "glossary" → "GL": a two-letter type tag that fits a calendar cell. */
function typeTag(type: string): string {
  const words = type.split(/[_\s-]+/).filter(Boolean);
  const tag = words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : (words[0] ?? '?').slice(0, 2);
  return tag.toUpperCase();
}

function ProposalChip({
  proposal,
  onError,
  onDrafted,
  flat = false,
  overdue = false,
}: {
  proposal: ContentCalendarProposal;
  onError: (msg: string) => void;
  onDrafted: () => void;
  /** In the list layout: no chip border. */
  flat?: boolean;
  /** Dated before today and still open. */
  overdue?: boolean;
}) {
  const draft = useDraftProposal();
  const dismiss = useDismissProposal();
  // Peec's raw score is not a 0–1 share (values like 3.0 occur), so show it as sent rather than ×100.
  const score =
    proposal.opportunityScore != null
      ? proposal.opportunityScore.toFixed(
          Number.isInteger(proposal.opportunityScore) ? 0 : Math.abs(proposal.opportunityScore) < 1 ? 2 : 1,
        )
      : null;
  return (
    <div
      className={flat ? 'text-body-sm' : 'rounded-sm border border-border-light bg-surface-primary px-1.5 py-1 text-body-xs shadow-sm'}
      title={[proposal.title, proposal.rationale].filter(Boolean).join('\n\n')}
    >
      {/* Titles wrap in full: a clamped title hid what the recommendation was. */}
      <div className="break-words font-medium leading-snug text-text-primary" title={proposal.title}>
        {proposal.title}
      </div>
      {overdue && (
        <div className="mt-0.5">
          <Badge variant="warning">Past date</Badge>
        </div>
      )}
      <div className="mt-0.5 flex items-center justify-between gap-1 text-text-tertiary">
        <span className="flex min-w-0 items-center gap-1">
          <span
            className="flex-none rounded-sm bg-surface-tertiary px-1 font-label text-[10px] leading-4 text-text-secondary"
            title={humanize(proposal.suggestedContentType)}
          >
            {typeTag(proposal.suggestedContentType)}
          </span>
          {score != null && (
            <span className="truncate tabular-nums" title="Peec opportunity score: higher means a bigger gap to close">
              {score}
            </span>
          )}
        </span>
        <span className="flex shrink-0 gap-1">
          <button
            onClick={() => draft.mutate(proposal.id, { onError: (e) => onError(errMessage(e)), onSuccess: onDrafted })}
            disabled={draft.isPending}
            className="text-accent hover:underline disabled:opacity-50"
          >
            {draft.isPending ? '…' : 'Draft'}
          </button>
          <button
            onClick={() => dismiss.mutate(proposal.id, { onError: (e) => onError(errMessage(e)) })}
            disabled={dismiss.isPending}
            aria-label="Dismiss"
            className="text-text-tertiary hover:text-text-primary disabled:opacity-50"
          >
            <X className="h-3 w-3" aria-hidden />
          </button>
        </span>
      </div>
    </div>
  );
}
